import {
  Button,
  Input,
  Mail,
  PhoneInput,
  User,
} from "@ews-admin/global-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ManagerFormData,
  managerFormSchema,
} from "../../../schemas/onboarding.schema";
import { useAppDispatch } from "../../../store/hooks";
import { reloadManagersPage } from "../../../store/reducers/managersSlice";
import { managerApi } from "../../../services/manager.api";
import {
  CivilityEnum,
  CivilityType,
  NotificationPrefEnum,
  RoleEnum,
} from "../../../types/user.definition";
import { Modal } from "../Modal/Modal";
import { dateInputToInstant } from "../../../utils/date-format-utils";

interface AddManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddManagerModal: React.FC<AddManagerModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
  } = useForm<ManagerFormData>({
    resolver: zodResolver(managerFormSchema(t)),
    // MED-1632: validate as the user types so the submit button can stay disabled
    // until the form is valid; civility has no pre-selection (MED-1543).
    mode: "onChange",
    defaultValues: {
      civility: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
  });

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: ManagerFormData) => {
    setIsSubmitting(true);
    try {
      await managerApi.createManager({
        photoUrl: "",
        email: data.email,
        civility: data.civility as CivilityType,
        roleName: RoleEnum.MANAGER,
        firstName: data.firstName,
        phoneNumber: data.phone,
        lastName: data.lastName,
        preferredNotificationMethods: [NotificationPrefEnum.EMAIL],
        healthcareFacilityId: null,
        ...(dateInputToInstant(data.dateOfBirth)
          ? { dateOfBirth: dateInputToInstant(data.dateOfBirth) }
          : {}),
      });
      toast.success(t("admin.managers.addModal.success"));
      // MED-1635: the list is paged by the server — reload the page shown
      dispatch(reloadManagersPage());
      onClose();
    } catch {
      toast.error(t("admin.managers.addModal.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !isSubmitting && onClose()}
      title={t("admin.managers.addModal.title")}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-4">
          {/* MED-1632: the schema requires a civility (MED-1543) but the modal had
              no field for it, so every submit failed validation silently. */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="add-manager-civility"
              className="text-sm font-medium text-gray-700"
            >
              {t("onboarding.manager.civility")}
              <span className="ml-0.5 text-red-500">*</span>
            </label>
            <Controller
              name="civility"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  id="add-manager-civility"
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t("onboarding.manager.civility")}</option>
                  <option value={CivilityEnum.MR}>{t("common.mr")}</option>
                  <option value={CivilityEnum.MRS}>{t("common.mrs")}</option>
                </select>
              )}
            />
            {errors.civility && (
              <p className="mt-1 text-sm text-red-600">
                {errors.civility.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t("onboarding.manager.firstName")}
              required
              placeholder={t("onboarding.manager.firstNamePlaceholder")}
              leftIcon={<User className="w-4 h-4" />}
              error={errors.firstName?.message}
              {...register("firstName")}
            />
            <Input
              label={t("onboarding.manager.lastName")}
              required
              placeholder={t("onboarding.manager.lastNamePlaceholder")}
              leftIcon={<User className="w-4 h-4" />}
              error={errors.lastName?.message}
              {...register("lastName")}
            />
          </div>

          <Input
            type="email"
            label={t("onboarding.manager.email")}
            required
            placeholder={t("onboarding.manager.emailPlaceholder")}
            leftIcon={<Mail className="w-4 h-4" />}
            error={errors.email?.message}
            {...register("email")}
          />

          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState }) => (
              <PhoneInput
                label={t("onboarding.manager.phone")}
                required
                {...field}
                error={fieldState.error?.message}
              />
            )}
          />

          <Input
            type="date"
            label={t("onboarding.manager.dateOfBirth")}
            error={errors.dateOfBirth?.message}
            {...register("dateOfBirth")}
          />
        </div>

        <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={!isValid || isSubmitting}>
            {isSubmitting
              ? t("common.processing")
              : t("admin.managers.addModal.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
